import { Box, Button, Popover } from '@primer/react';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { MeasurableGroupType } from '@upright/common/model/input/measurable-group/types';
import { Paragraph } from '@upright/ui/common/generic/components/typography';
import { Text } from '@upright/ui/common/generic/components/primer-custom/text';
import { netImpactLensTipPopOverKey, useRememberBoolean } from '@upright/ui/common/generic/utilities/storage';
import { useGetLensAndSetter } from '@upright/ui/views/model-entity-views/shared/impact-lenses/use-get-lens-and-setter';
import { useUnifiedShell } from '@upright/ui/views/unified-shell/use-unified-shell';
import styled from 'styled-components';
import { LensDataAccess, LensDataAvailability, SingleEntityLensData } from '../../../impact-lens-profile/types';
import { useNetImpactLensContext } from '../common/use-net-impact-lens-context';

interface Props {
  data: SingleEntityLensData<ImpactLensType>;
}

/** Whether the net impact chart has category bars a reader can open, so the tip applies. */
function hasOpenableCategoryBars(data: SingleEntityLensData<ImpactLensType>) {
  const notAvailable = data.availability === LensDataAvailability.UNAVAILABLE;
  const accessDenied = data.access === LensDataAccess.DENIED;
  const unweightedPortfolio = data.groupType === MeasurableGroupType.UNWEIGHTED_PORTFOLIO;
  return !notAvailable && !accessDenied && !unweightedPortfolio;
}

export const NetImpactLensTipPopover = (props: Props) => {
  const [popoverChecked, setPopoverChecked] = useRememberBoolean(netImpactLensTipPopOverKey);
  const { lens } = useGetLensAndSetter();
  // The unified profiles say the same thing in `NetImpactLensHint` above the chart.
  const unifiedShell = useUnifiedShell();

  if (lens !== ImpactLensType.UPRIGHT_NET_IMPACT || !hasOpenableCategoryBars(props.data) || unifiedShell.enabled) {
    return <></>;
  }

  return (
    <Popover
      open={!popoverChecked}
      caret="top"
      sx={{
        boxShadow: 'shadow.large',
        position: 'absolute',
        top: 24,
        left: [0, 0, '50%'],
        transform: ['none', 'none', 'translateX(-50%)'],
        zIndex: 98,
      }}
    >
      <Popover.Content sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
        <Text as="p" fontWeight="normal" lineHeight="condensed">
          <Text as="span" fontWeight="bold">
            Tip on this lens!{' '}
          </Text>
          Click on the gray category bars for a detailed breakdown of each impact category score.
        </Text>
        <Box>
          <Button
            size="small"
            onClick={() => {
              setPopoverChecked(true);
            }}
          >
            Got it
          </Button>
        </Box>
      </Popover.Content>
    </Popover>
  );
};

/**
 * The lens tip as a standing line above the chart, for the unified profiles. The popover opened
 * over the cost bars on first visit and had to be dismissed before the chart could be read
 * (PLT-7238, item 8); a hint that needs no dismissing says the same and covers nothing.
 */
export const NetImpactLensHint = () => {
  const context = useNetImpactLensContext();
  const unifiedShell = useUnifiedShell();
  if (!context?.data || !unifiedShell.enabled || context.options.staticMode || !hasOpenableCategoryBars(context.data)) {
    return null;
  }
  return (
    <Hint>
      <Paragraph size="md" height="sm" color="gray-600">
        Select a category bar to see its breakdown.
      </Paragraph>
    </Hint>
  );
};

const Hint = styled.div`
  margin-block-end: var(--space__md);
`;
